import React, { useState, useEffect, useCallback } from 'react';
import { 
  systemHealthService, 
  SystemHealthData, 
  HealthCheckResult,
  HealthHistoryEntry,
  Incident,
  UptimeStats,
  PingResult,
  AlertConfig
} from '../services/systemHealthService';
import { Skeleton, SkeletonCard } from '../components/Common/Skeleton';

// Tab type
type TabType = 'overview' | 'history' | 'incidents' | 'alerts' | 'ping';

const SystemHealth: React.FC = () => {
  const [healthData, setHealthData] = useState<SystemHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  
  // History & Stats
  const [uptimeStats, setUptimeStats] = useState<UptimeStats[]>([]);
  const [historyPeriod, setHistoryPeriod] = useState<number>(24);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  
  // Ping Test
  const [pingResults, setPingResults] = useState<PingResult[]>([]);
  const [isPinging, setIsPinging] = useState(false);
  
  // Alerts
  const [alertConfig, setAlertConfig] = useState<AlertConfig>(systemHealthService.getAlertConfig());
  const [alertEmail, setAlertEmail] = useState(alertConfig.email || '');

  const loadHealthData = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) setLoading(true);
      const data = await systemHealthService.getSystemHealth(forceRefresh);
      setHealthData(data);
      setLastRefreshTime(new Date());
      setUptimeStats(systemHealthService.getUptimeStats(historyPeriod));
      setIncidents(systemHealthService.getIncidents());
    } catch (error) {
      console.error('Error loading health data:', error);
    } finally {
      setLoading(false);
    }
  }, [historyPeriod]);

  useEffect(() => { loadHealthData(true); }, [loadHealthData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => loadHealthData(true), refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadHealthData]);

  const runPingTest = async () => {
    setIsPinging(true);
    try {
      const results = await systemHealthService.runPingTest();
      setPingResults(results);
    } catch (error) {
      console.error('Ping test failed:', error);
    } finally {
      setIsPinging(false);
    }
  };

  const saveAlerts = () => {
    const config: AlertConfig = {
      ...alertConfig,
      email: alertEmail
    };
    systemHealthService.saveAlertConfig(config);
    setAlertConfig(config);
    alert('Alert settings saved!');
  };

  const exportStatusPage = () => {
    const data = systemHealthService.generateStatusPageData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `status-page-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: 'healthy' | 'degraded' | 'down') => {
    const styles = {
      healthy: 'bg-green-100 text-green-800 border-green-200',
      degraded: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      down: 'bg-red-100 text-red-800 border-red-200'
    };
    const labels = { healthy: 'Operational', degraded: 'Degraded', down: 'Down' };
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${styles[status]}`}>
        <span className={`w-2 h-2 rounded-full ${status === 'healthy' ? 'bg-green-500 animate-pulse' : status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'}`} />
        {labels[status]}
      </span>
    );
  };

  // Uptime Timeline Component (GitHub-style)
  const UptimeTimeline: React.FC<{ service: string; hours: number }> = ({ service, hours }) => {
    const history = systemHealthService.getHistoryForService(service, hours);
    const slots = 90; // Number of time slots to show
    const slotDuration = (hours * 60 * 60 * 1000) / slots;
    const now = Date.now();
    
    const timeSlots = Array.from({ length: slots }, (_, i) => {
      const slotStart = now - (slots - i) * slotDuration;
      const slotEnd = slotStart + slotDuration;
      const entriesInSlot = history.filter(h => 
        h.timestamp.getTime() >= slotStart && h.timestamp.getTime() < slotEnd
      );
      
      if (entriesInSlot.length === 0) return 'unknown';
      const hasDown = entriesInSlot.some(e => e.status === 'down');
      const hasDegraded = entriesInSlot.some(e => e.status === 'degraded');
      if (hasDown) return 'down';
      if (hasDegraded) return 'degraded';
      return 'healthy';
    });

    return (
      <div className="flex gap-0.5">
        {timeSlots.map((status, i) => (
          <div
            key={i}
            className={`w-1.5 h-8 rounded-sm ${
              status === 'healthy' ? 'bg-green-500' :
              status === 'degraded' ? 'bg-yellow-500' :
              status === 'down' ? 'bg-red-500' :
              'bg-gray-200'
            }`}
            title={`${Math.round((slots - i) * (hours / slots))}h ago`}
          />
        ))}
      </div>
    );
  };

  // Response Time Chart Component
  const ResponseTimeChart: React.FC<{ service: string; hours: number }> = ({ service, hours }) => {
    const history = systemHealthService.getHistoryForService(service, hours);
    if (history.length === 0) return <div className="text-gray-400 text-sm">No data available</div>;
    
    const maxTime = Math.max(...history.map(h => h.responseTime), 100);
    const points = history.slice(-50).map((h, i, arr) => {
      const x = (i / (arr.length - 1 || 1)) * 100;
      const y = 100 - (h.responseTime / maxTime) * 100;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="relative h-20">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polyline
            fill="none"
            stroke="#3B82F6"
            strokeWidth="2"
            points={points}
          />
        </svg>
        <div className="absolute bottom-0 left-0 text-xs text-gray-400">0ms</div>
        <div className="absolute top-0 left-0 text-xs text-gray-400">{maxTime}ms</div>
      </div>
    );
  };

  // Service Dependencies Map
  const DependencyMap: React.FC = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Dependencies</h3>
      <div className="flex items-center justify-center gap-4 py-8">
        <div className={`px-4 py-3 rounded-lg border-2 ${healthData?.website.status === 'healthy' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
          <div className="text-sm font-medium">Website</div>
          <div className="text-xs text-gray-500">Frontend</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-8 h-0.5 bg-gray-300" />
          <span className="text-xs text-gray-400">calls</span>
        </div>
        <div className={`px-4 py-3 rounded-lg border-2 ${healthData?.api.status === 'healthy' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
          <div className="text-sm font-medium">API Server</div>
          <div className="text-xs text-gray-500">Backend</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-8 h-0.5 bg-gray-300" />
          <span className="text-xs text-gray-400">queries</span>
        </div>
        <div className={`px-4 py-3 rounded-lg border-2 ${healthData?.database.status === 'healthy' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
          <div className="text-sm font-medium">MongoDB</div>
          <div className="text-xs text-gray-500">Database</div>
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <div className={`px-4 py-3 rounded-lg border-2 ${healthData?.dashboard.status === 'healthy' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
          <div className="text-sm font-medium">Dashboard</div>
          <div className="text-xs text-gray-500">Admin Panel (You are here)</div>
        </div>
      </div>
    </div>
  );

  // Health Card Component
  const renderHealthCard = (check: HealthCheckResult, stats?: UptimeStats) => {
    const statusColors = {
      healthy: 'border-l-green-500',
      degraded: 'border-l-yellow-500',
      down: 'border-l-red-500'
    };

    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${statusColors[check.status]}`}>
        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{check.service}</h3>
              <p className="text-xs text-gray-500">{check.lastChecked.toLocaleTimeString()}</p>
            </div>
            {getStatusBadge(check.status)}
          </div>

          {/* Uptime Percentage */}
          {stats && (
            <div className="mb-3 p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Uptime ({historyPeriod}h)</span>
                <span className={`text-lg font-bold ${stats.uptimePercentage >= 99 ? 'text-green-600' : stats.uptimePercentage >= 95 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {stats.uptimePercentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                <div 
                  className={`h-1.5 rounded-full ${stats.uptimePercentage >= 99 ? 'bg-green-500' : stats.uptimePercentage >= 95 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${stats.uptimePercentage}%` }}
                />
              </div>
            </div>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Response Time</span>
              <span className="font-medium">{check.responseTime}ms</span>
            </div>
            {stats && (
              <div className="flex justify-between">
                <span className="text-gray-500">Avg Response</span>
                <span className="font-medium">{stats.avgResponseTime}ms</span>
              </div>
            )}
          </div>

          {check.explanation && check.status !== 'healthy' && (
            <div className={`mt-3 p-2 rounded text-xs ${check.status === 'down' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>
              {check.explanation}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Tab Navigation
  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'history', label: 'History', icon: '📈' },
    { id: 'incidents', label: 'Incidents', icon: '🚨' },
    { id: 'ping', label: 'Ping Test', icon: '🔍' },
    { id: 'alerts', label: 'Alerts', icon: '🔔' },
  ];

  if (loading && !healthData) {
    return (
      <div className="space-y-6">
        <Skeleton width="40%" height="2rem" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  const ongoingIncidents = incidents.filter(i => i.status === 'ongoing');
  const resolvedIncidents = incidents.filter(i => i.status === 'resolved').slice(-20).reverse();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health Monitor</h1>
          <p className="text-sm text-gray-500">Professional monitoring dashboard</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={refreshInterval} onChange={(e) => setRefreshInterval(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white">
            <option value={15}>15s</option>
            <option value={30}>30s</option>
            <option value={60}>1m</option>
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="sr-only" />
            <div className={`w-10 h-5 rounded-full transition-colors ${autoRefresh ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <div className={`w-4 h-4 mt-0.5 ml-0.5 bg-white rounded-full transition-transform ${autoRefresh ? 'translate-x-5' : ''}`} />
            </div>
            <span className="text-sm text-gray-600">Auto</span>
          </label>
          <button onClick={() => loadHealthData(true)} disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Checking...' : 'Refresh'}
          </button>
          <button onClick={exportStatusPage}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200">
            Export
          </button>
        </div>
      </div>

      {/* Overall Status Banner */}
      {healthData && (
        <div className={`rounded-xl p-6 ${
          healthData.overall === 'healthy' ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200' :
          healthData.overall === 'degraded' ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200' :
          'bg-gradient-to-r from-red-50 to-rose-50 border border-red-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                healthData.overall === 'healthy' ? 'bg-green-100' :
                healthData.overall === 'degraded' ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                <span className="text-3xl">{healthData.overall === 'healthy' ? '✓' : healthData.overall === 'degraded' ? '⚠' : '✗'}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {healthData.overall === 'healthy' ? 'All Systems Operational' :
                   healthData.overall === 'degraded' ? 'Partial Degradation' : 'System Outage'}
                </h2>
                <p className="text-sm text-gray-600">
                  {ongoingIncidents.length > 0 ? `${ongoingIncidents.length} ongoing incident(s)` : 'No active incidents'}
                </p>
              </div>
            </div>
            {lastRefreshTime && (
              <div className="text-right hidden sm:block">
                <p className="text-xs text-gray-500">Last updated</p>
                <p className="text-sm font-medium">{lastRefreshTime.toLocaleTimeString()}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <span className="mr-2">{tab.icon}</span>{tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && healthData && (
        <div className="space-y-6">
          {/* Health Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {renderHealthCard(healthData.api, uptimeStats.find(s => s.service === 'API Server'))}
            {renderHealthCard(healthData.database, uptimeStats.find(s => s.service === 'Database (MongoDB)'))}
            {renderHealthCard(healthData.website, uptimeStats.find(s => s.service === 'Website'))}
            {renderHealthCard(healthData.dashboard, uptimeStats.find(s => s.service === 'Dashboard'))}
          </div>

          {/* Uptime Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Uptime History</h3>
              <select value={historyPeriod} onChange={(e) => setHistoryPeriod(Number(e.target.value))}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1">
                <option value={1}>Last 1 hour</option>
                <option value={6}>Last 6 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={168}>Last 7 days</option>
              </select>
            </div>
            <div className="space-y-4">
              {['api', 'database', 'website', 'dashboard'].map(service => (
                <div key={service} className="flex items-center gap-4">
                  <div className="w-32 text-sm text-gray-600 capitalize">{service}</div>
                  <div className="flex-1">
                    <UptimeTimeline service={service} hours={historyPeriod} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
              <div className="flex items-center gap-1"><div className="w-3 h-3 bg-green-500 rounded" /> Healthy</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 bg-yellow-500 rounded" /> Degraded</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded" /> Down</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 bg-gray-200 rounded" /> No data</div>
            </div>
          </div>

          {/* Dependencies Map */}
          <DependencyMap />
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Response Time Trends</h3>
              <select value={historyPeriod} onChange={(e) => setHistoryPeriod(Number(e.target.value))}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1">
                <option value={1}>Last 1 hour</option>
                <option value={6}>Last 6 hours</option>
                <option value={24}>Last 24 hours</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {['api', 'database', 'website', 'dashboard'].map(service => (
                <div key={service} className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 capitalize">{service}</h4>
                  <ResponseTimeChart service={service} hours={historyPeriod} />
                </div>
              ))}
            </div>
          </div>

          {/* Health Check Logs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Health Checks</h3>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-600">Time</th>
                    <th className="px-4 py-2 text-left text-gray-600">Service</th>
                    <th className="px-4 py-2 text-left text-gray-600">Status</th>
                    <th className="px-4 py-2 text-left text-gray-600">Response</th>
                  </tr>
                </thead>
                <tbody>
                  {systemHealthService.getHistory().slice(-50).reverse().map((entry, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-4 py-2 text-gray-500">{entry.timestamp.toLocaleTimeString()}</td>
                      <td className="px-4 py-2 capitalize">{entry.service}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          entry.status === 'healthy' ? 'bg-green-100 text-green-700' :
                          entry.status === 'degraded' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>{entry.status}</span>
                      </td>
                      <td className="px-4 py-2">{entry.responseTime}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Incidents Tab */}
      {activeTab === 'incidents' && (
        <div className="space-y-6">
          {/* Ongoing Incidents */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              🔴 Ongoing Incidents ({ongoingIncidents.length})
            </h3>
            {ongoingIncidents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl">✅</span>
                <p className="mt-2">No ongoing incidents</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ongoingIncidents.map(incident => (
                  <div key={incident.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-red-900 capitalize">{incident.service}</h4>
                        <p className="text-sm text-red-700 mt-1">{incident.description}</p>
                      </div>
                      <span className="text-xs text-red-600">
                        Started: {incident.startTime.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Resolved Incidents */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📋 Incident History ({resolvedIncidents.length})
            </h3>
            {resolvedIncidents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No incident history</div>
            ) : (
              <div className="space-y-3">
                {resolvedIncidents.map(incident => (
                  <div key={incident.id} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 capitalize">{incident.service}</h4>
                        <p className="text-sm text-gray-600 mt-1">{incident.description}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Duration: {incident.duration ? `${Math.round(incident.duration / 60)} minutes` : 'Unknown'}
                        </p>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>Started: {incident.startTime.toLocaleString()}</div>
                        <div>Resolved: {incident.endTime?.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ping Test Tab */}
      {activeTab === 'ping' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Connectivity Test</h3>
                <p className="text-sm text-gray-500">Run a manual ping test to all services</p>
              </div>
              <button onClick={runPingTest} disabled={isPinging}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {isPinging ? '🔄 Testing...' : '🚀 Run Ping Test'}
              </button>
            </div>

            {pingResults.length > 0 && (
              <div className="space-y-3">
                {pingResults.map((result, i) => (
                  <div key={i} className={`p-4 rounded-lg border ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{result.success ? '✅' : '❌'}</span>
                        <div>
                          <h4 className="font-medium">{result.service}</h4>
                          <p className="text-sm text-gray-500">
                            {result.success ? `Response: ${result.responseTime}ms` : result.error}
                          </p>
                        </div>
                      </div>
                      {result.statusCode && (
                        <span className={`px-2 py-1 rounded text-sm ${result.statusCode === 200 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          HTTP {result.statusCode}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-500 text-right">
                  Test completed at {pingResults[0]?.timestamp.toLocaleTimeString()}
                </p>
              </div>
            )}

            {pingResults.length === 0 && !isPinging && (
              <div className="text-center py-12 text-gray-500">
                <span className="text-4xl">🔍</span>
                <p className="mt-2">Click "Run Ping Test" to check connectivity</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Alert Configuration</h3>
            
            <div className="space-y-6">
              {/* Enable Alerts */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">Enable Alerts</h4>
                  <p className="text-sm text-gray-500">Receive notifications when services go down</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={alertConfig.enabled}
                    onChange={(e) => setAlertConfig({ ...alertConfig, enabled: e.target.checked })}
                    className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input type="email" value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                <p className="text-xs text-gray-500 mt-1">Note: Email alerts require backend integration</p>
              </div>

              {/* Notification Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notify When</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={alertConfig.notifyOnDown}
                      onChange={(e) => setAlertConfig({ ...alertConfig, notifyOnDown: e.target.checked })}
                      className="rounded text-blue-600" />
                    <span className="text-sm">Service goes down</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={alertConfig.notifyOnDegraded}
                      onChange={(e) => setAlertConfig({ ...alertConfig, notifyOnDegraded: e.target.checked })}
                      className="rounded text-blue-600" />
                    <span className="text-sm">Service is degraded</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={alertConfig.notifyOnRecovery}
                      onChange={(e) => setAlertConfig({ ...alertConfig, notifyOnRecovery: e.target.checked })}
                      className="rounded text-blue-600" />
                    <span className="text-sm">Service recovers</span>
                  </label>
                </div>
              </div>

              {/* Services to Monitor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Services to Monitor</label>
                <div className="grid grid-cols-2 gap-2">
                  {['api', 'database', 'website', 'dashboard'].map(service => (
                    <label key={service} className="flex items-center gap-2">
                      <input type="checkbox" checked={alertConfig.services.includes(service)}
                        onChange={(e) => {
                          const services = e.target.checked 
                            ? [...alertConfig.services, service]
                            : alertConfig.services.filter(s => s !== service);
                          setAlertConfig({ ...alertConfig, services });
                        }}
                        className="rounded text-blue-600" />
                      <span className="text-sm capitalize">{service}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button onClick={saveAlerts}
                className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700">
                Save Alert Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealth;
